import * as React from 'react';
import { createPortal } from 'react-dom';

type Props = {
  /** CSS selector for the liveblog root so we can scope the observer (e.g. '#liveblog') */
  rootSelector: string;
  /** CSS selector for the ad slots created by the widget (e.g. '.react-ad-slot') */
  slotSelector: string;
  /** What to render into each slot */
  render: (index: number | undefined) => React.ReactNode;
};

export default function AdPortals(props: Props) {
  const { rootSelector, slotSelector, render } = props;
  const [targets, setTargets] = React.useState<HTMLElement[]>([]);

  React.useEffect(function () {
    if (typeof document === 'undefined') return;

    function collect(root: Element | Document) {
      const els = Array.from(root.querySelectorAll(slotSelector)) as HTMLElement[];
      // Keep only elements still in the DOM
      setTargets(function () {
        return els;
      });
    }

    // Wait for the liveblog root (it might be mounted later)
    let cleanupObserver: (() => void) | null = null;

    function start(rootEl: Element) {
      collect(rootEl);

      // Observe additions/removals within the liveblog subtree
      const obs = new MutationObserver(function () {
        collect(rootEl);
      });
      obs.observe(rootEl, { childList: true, subtree: true });

      // Also re-collect on resize/scroll just in case of lazy inserts (optional)
      const onRecollect = function () { collect(rootEl); };
      window.addEventListener('resize', onRecollect);
      window.addEventListener('scroll', onRecollect, { passive: true });

      cleanupObserver = function () {
        obs.disconnect();
        window.removeEventListener('resize', onRecollect);
        window.removeEventListener('scroll', onRecollect);
      };
    }

    const rootNow = document.querySelector(rootSelector);
    if (rootNow) {
      start(rootNow);
    } else {
      // If the root isn't there yet, watch the whole document briefly
      const bootObs = new MutationObserver(function () {
        const el = document.querySelector(rootSelector);
        if (el) {
          bootObs.disconnect();
          start(el);
        }
      });
      bootObs.observe(document.documentElement, { childList: true, subtree: true });
      cleanupObserver = function () { bootObs.disconnect(); };
    }

    return function () {
      if (cleanupObserver) cleanupObserver();
      setTargets([]);
    };
  }, [rootSelector, slotSelector]);

  // Render a portal per target. Key by a stable id/data attribute if available.
  return (
    <>
      {targets.map(function (el) {
        const key = el.getAttribute('data-react-ad-id') ?? el.id ?? `${slotSelector}-${Math.random()}`;
        const index = el.getAttribute('data-react-ad-id');
        return createPortal(render(index ? parseInt(index) : 0) as any, el, key);
      })}
    </>
  );
}